import { supabase } from '../lib/supabase';

export const reviewService = {
  // 1. Kullanıcı için: Yeni yorum gönder (Varsayılan durum: 'pending')
  async createReview(spotId, rating, comment, imageUrls = []) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Yorum yapmak için giriş yapmalısınız.');

    const { data, error } = await supabase?.from('spot_reviews')?.insert({
        user_id: user?.id,
        spot_id: spotId,
        rating,
        comment,
        image_urls: imageUrls,
        status: 'pending' // Spam koruması: İlk başta onay bekliyor
      })?.select()?.single();

    if (error) throw error;

    // Convert snake_case to camelCase for React
    return {
      id: data?.id,
      userId: data?.user_id,
      spotId: data?.spot_id,
      rating: data?.rating,
      comment: data?.comment,
      imageUrls: data?.image_urls,
      status: data?.status,
      createdAt: data?.created_at
    };
  },

  // 2. Spot Detay Sayfası için: Sadece ONAYLANMIŞ yorumları getir
  async getApprovedReviewsForSpot(spotId) {
    const { data, error } = await supabase?.from('spot_reviews')?.select(`
        id,
        rating,
        comment,
        image_urls,
        created_at,
        user:user_profiles!user_id(full_name, avatar_url)
      `)?.eq('spot_id', spotId)?.eq('status', 'approved')?.order('created_at', { ascending: false });

    if (error) throw error;

    // Convert snake_case to camelCase for React
    return (data || [])?.map(review => ({
      id: review?.id,
      rating: review?.rating,
      comment: review?.comment,
      imageUrls: review?.image_urls,
      createdAt: review?.created_at,
      user: {
        fullName: review?.user?.full_name,
        avatarUrl: review?.user?.avatar_url
      }
    }));
  },

  // 2.5 Event Detay Sayfası için: Sadece ONAYLANMIŞ event yorumları getir
  async getEventReviews(eventId) {
    const { data, error } = await supabase?.from('event_reviews')?.select(`
        id,
        rating,
        comment,
        image_urls,
        created_at,
        user:user_profiles!user_id(full_name, avatar_url)
      `)?.eq('event_id', eventId)?.eq('status', 'approved')?.order('created_at', { ascending: false });

    if (error) throw error;

    // Convert snake_case to camelCase for React
    return (data || [])?.map(review => ({
      id: review?.id,
      rating: review?.rating,
      comment: review?.comment,
      imageUrls: review?.image_urls,
      createdAt: review?.created_at,
      userName: review?.user?.full_name || 'Anonim Kullanıcı',
      userAvatar: review?.user?.avatar_url,
      userAvatarAlt: `${review?.user?.full_name || 'User'} avatar`,
      date: new Date(review?.created_at)?.toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }));
  },

  // 2.6 Event için istatistikleri getir
  async getEventReviewStats(eventId) {
    const { data, error } = await supabase?.from('event_reviews')?.select('rating')?.eq('event_id', eventId)?.eq('status', 'approved');

    if (error) throw error;

    const reviews = data || [];
    const totalReviews = reviews?.length;

    if (totalReviews === 0) {
      return {
        averageRating: 0,
        totalReviews: 0,
        ratingDistribution: [
          { stars: 5, count: 0, percentage: 0 },
          { stars: 4, count: 0, percentage: 0 },
          { stars: 3, count: 0, percentage: 0 },
          { stars: 2, count: 0, percentage: 0 },
          { stars: 1, count: 0, percentage: 0 }
        ]
      };
    }

    // Calculate average rating
    const totalRating = reviews?.reduce((sum, review) => sum + review?.rating, 0);
    const averageRating = (totalRating / totalReviews)?.toFixed(1);

    // Calculate rating distribution
    const distribution = [5, 4, 3, 2, 1]?.map(stars => {
      const count = reviews?.filter(r => r?.rating === stars)?.length;
      const percentage = (count / totalReviews) * 100;
      return { stars, count, percentage };
    });

    return {
      averageRating: parseFloat(averageRating),
      totalReviews,
      ratingDistribution: distribution
    };
  },

  // 2.7 Kullanıcının event için yorum yapıp yapamayacağını kontrol et
  async canUserReview(eventId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) return false;

    // Check if user has a registration for this event
    const { data, error } = await supabase?.from('event_registrations')?.select('id')?.eq('event_id', eventId)?.eq('user_id', user?.id)?.eq('status', 'approved')?.single();

    if (error && error?.code !== 'PGRST116') {
      console.error('Error checking user registration:', error);
      return false;
    }

    return !!data;
  },

  // 2.8 Kullanıcının event için mevcut yorumunu getir
  async getUserReview(eventId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) return null;

    const { data, error } = await supabase?.from('event_reviews')?.select('id, rating, comment, image_urls, created_at')?.eq('event_id', eventId)?.eq('user_id', user?.id)?.single();

    if (error && error?.code !== 'PGRST116') {
      console.error('Error fetching user review:', error);
      return null;
    }

    if (!data) return null;

    return {
      id: data?.id,
      rating: data?.rating,
      comment: data?.comment,
      imageUrls: data?.image_urls,
      createdAt: data?.created_at
    };
  },

  // 2.9 Event için yeni yorum oluştur
  async createEventReview(eventId, rating, comment, imageUrls = []) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Yorum yapmak için giriş yapmalısınız.');

    const { data, error } = await supabase?.from('event_reviews')?.insert({
        user_id: user?.id,
        event_id: eventId,
        rating,
        comment,
        image_urls: imageUrls,
        status: 'approved' // Event reviews are auto-approved
      })?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      userId: data?.user_id,
      eventId: data?.event_id,
      rating: data?.rating,
      comment: data?.comment,
      imageUrls: data?.image_urls,
      status: data?.status,
      createdAt: data?.created_at
    };
  },

  // 3.0 Event yorumunu güncelle
  async updateEventReview(reviewId, rating, comment, imageUrls = []) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Yorumu güncellemek için giriş yapmalısınız.');

    const { data, error } = await supabase?.from('event_reviews')?.update({
        rating,
        comment,
        image_urls: imageUrls
      })?.eq('id', reviewId)?.eq('user_id', user?.id)?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      rating: data?.rating,
      comment: data?.comment,
      imageUrls: data?.image_urls,
      updatedAt: data?.updated_at
    };
  },

  // 3.1 Event yorumunu sil
  async deleteEventReview(reviewId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Yorumu silmek için giriş yapmalısınız.');

    const { error } = await supabase?.from('event_reviews')?.delete()?.eq('id', reviewId)?.eq('user_id', user?.id);

    if (error) throw error;
    return true;
  },

  // 3. Admin Paneli için: Bekleyen (pending) yorumları getir
  async getPendingReviews() {
    // Fetch pending spot reviews
    const { data: spotReviews, error: spotError } = await supabase?.from('spot_reviews')?.select(`
        id,
        rating,
        comment,
        image_urls,
        created_at,
        user:user_profiles!user_id(full_name, avatar_url),
        spot:spots!spot_id(name)
      `)?.eq('status', 'pending')?.order('created_at', { ascending: true });

    if (spotError) console.error('Error fetching spot reviews:', spotError);

    // Fetch pending event reviews
    const { data: eventReviews, error: eventError } = await supabase?.from('event_reviews')?.select(`
        id,
        rating,
        comment,
        image_urls,
        created_at,
        user:user_profiles!user_id(full_name, avatar_url),
        event:events!event_id(title)
      `)?.eq('status', 'pending')?.order('created_at', { ascending: true });

    if (eventError) console.error('Error fetching event reviews:', eventError);

    // Fetch pending blog comments
    const { data: blogComments, error: blogError } = await supabase?.from('blog_comments')?.select(`
        id,
        content,
        created_at,
        user:user_profiles!user_id(full_name, avatar_url),
        post:posts!post_id(title)
      `)?.eq('status', 'pending')?.order('created_at', { ascending: true });

    if (blogError) console.error('Error fetching blog comments:', blogError);

    // Convert all to unified format
    const spotReviewsFormatted = (spotReviews || [])?.map(review => ({
      id: review?.id,
      type: 'spot_review',
      rating: review?.rating,
      comment: review?.comment,
      imageUrls: review?.image_urls,
      createdAt: review?.created_at,
      user: {
        fullName: review?.user?.full_name,
        avatarUrl: review?.user?.avatar_url
      },
      spot: {
        name: review?.spot?.name
      }
    }));

    const eventReviewsFormatted = (eventReviews || [])?.map(review => ({
      id: review?.id,
      type: 'event_review',
      rating: review?.rating,
      comment: review?.comment,
      imageUrls: review?.image_urls,
      createdAt: review?.created_at,
      user: {
        fullName: review?.user?.full_name,
        avatarUrl: review?.user?.avatar_url
      },
      event: {
        title: review?.event?.title
      }
    }));

    const blogCommentsFormatted = (blogComments || [])?.map(comment => ({
      id: comment?.id,
      type: 'blog_comment',
      rating: null, // Blog comments don't have ratings
      comment: comment?.content,
      imageUrls: [],
      createdAt: comment?.created_at,
      user: {
        fullName: comment?.user?.full_name,
        avatarUrl: comment?.user?.avatar_url
      },
      post: {
        title: comment?.post?.title
      }
    }));

    // Combine all and sort by creation date
    const allReviews = [...spotReviewsFormatted, ...eventReviewsFormatted, ...blogCommentsFormatted];
    allReviews?.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

    return allReviews;
  },

  // 4. Admin Paneli için: Yorum durumunu güncelle (Onayla/Reddet)
  async updateReviewStatus(reviewId, status, reviewType = 'spot_review') {
    if (!['approved', 'rejected']?.includes(status)) {
      throw new Error('Geçersiz durum.');
    }

    let table;
    switch (reviewType) {
      case 'spot_review':
        table = 'spot_reviews';
        break;
      case 'event_review':
        table = 'event_reviews';
        break;
      case 'blog_comment':
        table = 'blog_comments';
        break;
      default:
        table = 'spot_reviews';
    }

    const { error } = await supabase?.from(table)?.update({ status })?.eq('id', reviewId);

    if (error) throw error;
    return true;
  },

  // ============================================
  // PRODUCT REVIEWS (Official Store)
  // ============================================

  // Get approved reviews for a product
  async getProductReviews(productId) {
    const { data, error } = await supabase
      ?.from('product_reviews')
      ?.select(`
        id,
        rating,
        comment,
        image_urls,
        created_at,
        user:user_profiles!user_id(full_name, avatar_url)
      `)
      ?.eq('product_id', productId)
      ?.eq('status', 'approved')
      ?.order('created_at', { ascending: false });

    if (error) throw error;

    return (data || [])?.map(review => ({
      id: review?.id,
      rating: review?.rating,
      comment: review?.comment,
      imageUrls: review?.image_urls,
      createdAt: review?.created_at,
      userName: review?.user?.full_name || 'Anonim Kullanıcı',
      userAvatar: review?.user?.avatar_url,
      userAvatarAlt: `${review?.user?.full_name || 'User'} avatar`,
      date: new Date(review?.created_at)?.toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }));
  },

  // Get product review statistics
  async getProductReviewStats(productId) {
    const { data, error } = await supabase
      ?.from('product_reviews')
      ?.select('rating')
      ?.eq('product_id', productId)
      ?.eq('status', 'approved');

    if (error) throw error;

    const reviews = data || [];
    const totalReviews = reviews?.length;

    if (totalReviews === 0) {
      return {
        averageRating: 0,
        totalReviews: 0,
        ratingDistribution: [
          { stars: 5, count: 0, percentage: 0 },
          { stars: 4, count: 0, percentage: 0 },
          { stars: 3, count: 0, percentage: 0 },
          { stars: 2, count: 0, percentage: 0 },
          { stars: 1, count: 0, percentage: 0 }
        ]
      };
    }

    const totalRating = reviews?.reduce((sum, review) => sum + review?.rating, 0);
    const averageRating = (totalRating / totalReviews)?.toFixed(1);

    const distribution = [5, 4, 3, 2, 1]?.map(stars => {
      const count = reviews?.filter(r => r?.rating === stars)?.length;
      const percentage = (count / totalReviews) * 100;
      return { stars, count, percentage };
    });

    return {
      averageRating: parseFloat(averageRating),
      totalReviews,
      ratingDistribution: distribution
    };
  },

  // Get user's review for a product
  async getUserProductReview(productId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) return null;

    const { data, error } = await supabase
      ?.from('product_reviews')
      ?.select('id, rating, comment, image_urls, created_at')
      ?.eq('product_id', productId)
      ?.eq('user_id', user?.id)
      ?.single();

    if (error && error?.code !== 'PGRST116') {
      console.error('Error fetching user product review:', error);
      return null;
    }

    if (!data) return null;

    return {
      id: data?.id,
      rating: data?.rating,
      comment: data?.comment,
      imageUrls: data?.image_urls,
      createdAt: data?.created_at
    };
  },

  // Create product review
  async createProductReview(productId, rating, comment, imageUrls = []) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Yorum yapmak için giriş yapmalısınız.');

    const { data, error } = await supabase
      ?.from('product_reviews')
      ?.insert({
        user_id: user?.id,
        product_id: productId,
        rating,
        comment,
        image_urls: imageUrls,
        status: 'approved'
      })
      ?.select()
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      userId: data?.user_id,
      productId: data?.product_id,
      rating: data?.rating,
      comment: data?.comment,
      imageUrls: data?.image_urls,
      status: data?.status,
      createdAt: data?.created_at
    };
  },

  // Update product review
  async updateProductReview(reviewId, rating, comment, imageUrls = []) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Yorumu güncellemek için giriş yapmalısınız.');

    const { data, error } = await supabase
      ?.from('product_reviews')
      ?.update({
        rating,
        comment,
        image_urls: imageUrls
      })
      ?.eq('id', reviewId)
      ?.eq('user_id', user?.id)
      ?.select()
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      rating: data?.rating,
      comment: data?.comment,
      imageUrls: data?.image_urls,
      updatedAt: data?.updated_at
    };
  },

  // Delete product review
  async deleteProductReview(reviewId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Yorumu silmek için giriş yapmalısınız.');

    const { error } = await supabase
      ?.from('product_reviews')
      ?.delete()
      ?.eq('id', reviewId)
      ?.eq('user_id', user?.id);

    if (error) throw error;
    return true;
  }
};